import React from 'react';
import PropTypes from 'prop-types';
import {
  CheckCircleOutlined,
  WarningOutlined,
  ExclamationCircleOutlined,
} from '@ant-design/icons';

import Colors from '../../../../../constants/Colors';
import RedistributeHourBadge from './RedistributeHourBadge';
import { formatHours } from './redistributionHelpers';

const formatTotal = (hours = 0, otHours = 0, doubleOTHours = 0) => (
  formatHours(Math.abs(hours + otHours + doubleOTHours))
);

const getHeaderIcon = (status) => {
  switch (status) {
    case 'complete':
      return (
        <CheckCircleOutlined
          style={{ fontSize: 20, color: Colors.GREEN_600 }}
          aria-label="All distributed"
        />
      );
    case 'overrun':
      return (
        <WarningOutlined
          style={{ fontSize: 20, color: Colors.RED_600 }}
          aria-label="Overrun warning"
        />
      );
    case 'default':
      return (
        <ExclamationCircleOutlined
          style={{ fontSize: 20, color: '#EA580C' }}
          aria-label="Unassigned hours summary"
        />
      );
    default:
      return (
        <CheckCircleOutlined
          style={{ fontSize: 20, color: Colors.BLUE_700 }}
          aria-label="Cost coded summary"
        />
      );
  }
};

export default function RedistributeSummaryCard({
  title,
  redistributeStatus,
  isEquipment,
  hours,
  otHours,
  doubleOTHours,
}) {
  const totalStr = formatTotal(hours, otHours, doubleOTHours);

  let hourValueVariant;
  const titleClassName = ['ttg-redistribute-summary-title'];
  const totalClassName = ['ttg-redistribute-summary-total-hours'];
  const cardClassName = ['ttg-redistribute-card'];

  switch (redistributeStatus) {
    case 'default':
      cardClassName.push('ttg-redistribute-card-unassigned-default');
      titleClassName.push('ttg-redistribute-summary-title-unassigned-default');
      totalClassName.push('ttg-redistribute-summary-total-hours-unassigned-default');
      hourValueVariant = 'default';
      break;
    case 'complete':
      cardClassName.push('ttg-redistribute-card-unassigned-complete');
      titleClassName.push('ttg-redistribute-summary-title-complete');
      totalClassName.push('ttg-redistribute-summary-total-hours-unassigned-complete');
      hourValueVariant = 'complete';
      break;
    case 'overrun':
      cardClassName.push('ttg-redistribute-card-unassigned-overrun');
      titleClassName.push('ttg-redistribute-summary-title-overrun');
      totalClassName.push('ttg-redistribute-summary-total-hours-overrun');
      hourValueVariant = 'overrun';
      break;
    default:
      cardClassName.push('ttg-redistribute-card-cost-coded');
      titleClassName.push('ttg-redistribute-summary-title-cost-coded');
      totalClassName.push('ttg-redistribute-summary-total-hours-cost-coded');
      hourValueVariant = 'costCoded';
      break;
  }

  const headerIcon = getHeaderIcon(redistributeStatus);

  return (
    <div className={cardClassName.join(' ')}>
      <div className="ttg-redistribute-summary-header">
        <div className="ttg-redistribute-summary-header-leading">
          {headerIcon}
          <span className={titleClassName.join(' ')}>{title}</span>
        </div>
        <span className={totalClassName.join(' ')}>{totalStr}</span>
      </div>
      {!isEquipment && redistributeStatus !== 'overrun' && (
        <div className="ttg-redistribute-summary-badge-row">
          <RedistributeHourBadge
            variant="REG"
            value={hours}
            valueStylePreset={hourValueVariant}
          />
          <RedistributeHourBadge
            variant="OT"
            value={otHours}
            valueStylePreset={hourValueVariant}
          />
          <RedistributeHourBadge
            variant="DT"
            value={doubleOTHours}
            valueStylePreset={hourValueVariant}
          />
        </div>
      )}
    </div>
  );
}

RedistributeSummaryCard.propTypes = {
  title: PropTypes.string.isRequired,
  redistributeStatus: PropTypes.oneOf(['default', 'complete', 'overrun']),
  isEquipment: PropTypes.bool.isRequired,
  hours: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  otHours: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  doubleOTHours: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

RedistributeSummaryCard.defaultProps = {
  redistributeStatus: undefined,
};
